import { AudioLines, Bot, PhoneIncoming, PhoneOutgoing } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import type { CompanyCallLog } from '@/api/callLogs'
import { getStatusStyle } from './historyHelpers'
import { InlineRecordingPlayer } from './InlineRecordingPlayer'

interface Props {
  logs: CompanyCallLog[]
  companyId: number
  expandedLogId: number | null
  onToggleExpand: (logId: number | null) => void
}

/** Vue compacte mobile (md-): cards empilées. */
export function CallLogsMobile({
  logs,
  companyId,
  expandedLogId,
  onToggleExpand,
}: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? fr : enUS

  return (
    <div className="md:hidden space-y-2">
      {logs.map((log) => {
        const isExpanded = expandedLogId === log.id
        const style = getStatusStyle(log.status)
        const isInbound = log.direction === 'inbound'
        const when = format(new Date(log.started_at), 'Pp', {
          locale: dateLocale,
        })
        const partyLabel =
          (isInbound && log.agent_name) || (!isInbound && log.agent_name)
            ? log.agent_name
            : log.display_destination || log.from_number || log.to_number

        return (
          <div
            key={log.id}
            className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                    isInbound
                      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300'
                      : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300',
                  )}
                >
                  {isInbound ? (
                    <PhoneIncoming className="h-4 w-4" />
                  ) : (
                    <PhoneOutgoing className="h-4 w-4" />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate flex items-center gap-1.5">
                    {partyLabel || '—'}
                    {log.answer_type === 'ai' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300 flex-shrink-0">
                        <Bot className="w-2.5 h-2.5" />
                        {t('manager.history.table.aiBadge')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {when}
                  </p>
                </div>
              </div>

              <span
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0',
                  style.className,
                )}
              >
                <span
                  className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    style.dotClass,
                    style.pulse && 'animate-pulse',
                  )}
                />
                {t(`manager.history.statuses.${style.labelKey}`)}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span className="font-mono">
                {log.duration_formatted || '0s'}
              </span>
              {!log.recording_locked && (log.has_recordings || log.ai_audio_url) && (
                <button
                  type="button"
                  onClick={() =>
                    onToggleExpand(isExpanded ? null : log.id)
                  }
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 rounded-md transition-colors',
                    isExpanded
                      ? 'bg-indigo-500 text-white'
                      : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
                  )}
                >
                  <AudioLines className="h-3 w-3" />
                  {t('manager.history.table.recording')}
                </button>
              )}
            </div>

            {isExpanded && (
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center">
                <InlineRecordingPlayer
                  companyId={companyId}
                  callId={log.id}
                  aiAudioUrl={log.ai_audio_url}
                  onClose={() => onToggleExpand(null)}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
